import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Game {
  id: string;
  name: string;
  thumbnail: string;
  provider_name: string;
}

export function RecentGames() {
  const { isAuthenticated } = useAuth();
  const [recentGames, setRecentGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      setCanScrollRight(container.scrollLeft < maxScrollLeft - 1);
    }
  };

  const handleScroll = (scrollOffset: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: scrollOffset, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchRecentGames = async () => {
      try {
        const response = await api.get('/games/recent');
        if (response.data.success) {
          setRecentGames(response.data.games);
        }
      } catch (err) {
        console.error("Error fetching recent games:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentGames();
  }, [isAuthenticated]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScroll();
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [recentGames]);

  if (loading || recentGames.length === 0 || !isAuthenticated) {
    return null;
  }

  return (
    <div className="mb-12 relative overflow-hidden">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-white-700">Jogados Recentemente</h2>
      </div>
      <div className="absolute top-1/2 -left-5 -translate-y-1/2 z-10 hidden md:block">
        {canScrollLeft && (
            <button onClick={() => handleScroll(-300)} className="p-2 rounded-full bg-primary hover:bg-secondary text-dark transition-all duration-300 shadow-lg">
                <ChevronLeft size={24} />
            </button>
        )}
      </div>
      <div className="absolute top-1/2 -right-5 -translate-y-1/2 z-10 hidden md:block">
        {canScrollRight && (
            <button onClick={() => handleScroll(300)} className="p-2 rounded-full bg-primary hover:bg-secondary text-dark transition-all duration-300 shadow-lg">
                <ChevronRight size={24} />
            </button>
        )}
      </div>
      <div ref={scrollContainerRef} className="flex overflow-x-auto space-x-4 pb-4 no-scrollbar">
        {recentGames.map((game) => (
          <Link to={`/game/${game.id}`} key={game.id} className="block flex-shrink-0 w-40 group">
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer">
              <img src={game.thumbnail} alt={game.name} className="w-full h-24 object-cover transition-transform duration-300 group-hover:scale-110" />
              <div className="p-2">
                <h3 className="font-semibold text-gray-800 text-xs truncate">{game.name}</h3>
                <p className="text-gray-500 text-[10px]">{game.provider_name}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
